import { CommodityDto, SuggestedCommodityCodeDto } from '@billing/app/api'
import { FormProps, SelectOption } from '@billing/app/models'
import useFormWithSchema from '@billing/hooks/use-form-with-schema.hook'
import {
  Autocomplete,
  Card,
  CardContent,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { CustomInputAdornment, useMinimalsTheme } from '@tom-ui/ui'
import { matchSorter } from 'match-sorter'
import { useEffect } from 'react'
import { Controller } from 'react-hook-form'
import { CommodityAttributeForm } from './CommodityAttributeForm'
import { CommodityFormMapper } from './commodity-form.mapper'
import { CommodityFormProfile, defaultValues } from './commodity-form.profile'
import { schema } from './commodity-form.scheme'

interface Props extends FormProps {
  commodity?: CommodityDto
  packageOptions: SelectOption[]
  suggestedCode?: SuggestedCommodityCodeDto
  parentCommodityId?: number
  catalogueId?: number
  onSubmit: (formValues: CommodityFormProfile) => Promise<void> | void
}

export const CommodityForm = ({
  id,
  commodity,
  packageOptions,
  suggestedCode,
  parentCommodityId,
  catalogueId,
  onSubmit,
}: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { handleSubmit, formState, control, reset, setValue } =
    useFormWithSchema<CommodityFormProfile>(schema(), defaultValues)

  useEffect(() => {
    if (commodity) {
      reset(
        CommodityFormMapper.mapDtoToFormValues(
          commodity,
          packageOptions.filter(x => commodity.packageIds.find(p => p === +x.value)),
        ),
      )
    } else {
      reset({
        ...defaultValues,
        parentCommodityId,
        catalogueId,
        code: suggestedCode?.code ?? '',
      })
    }
  }, [
    reset,
    setValue,
    commodity,
    packageOptions,
    suggestedCode?.code,
    parentCommodityId,
    catalogueId,
  ])

  return (
    <Stack
      id={id}
      component='form'
      noValidate
      autoComplete='off'
      onSubmit={handleSubmit(onSubmit)}
      pb={theme.customSpacing.xs}
      gap={theme.customSpacing.l}
    >
      <Card>
        <CardContent>
          <Stack gap={theme.customSpacing.m}>
            <Typography variant='subtitle1'>
              {t('commodityDetails', 'Commodity details')}
            </Typography>

            <Controller
              control={control}
              name='code'
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!formState.errors['code']}
                  label={t('code', 'Code')}
                  fullWidth
                  required
                  helperText={
                    formState.errors['code']
                      ? t('fieldIsRequired', 'Field is required')
                      : t(
                          'pleaseMakeSureItsAUniqueField',
                          "Please make sure it's a unique {field}",
                          {
                            field: t('code', 'Code'),
                          },
                        )
                  }
                />
              )}
            />

            <Controller
              control={control}
              name='name'
              render={({ field }) => (
                <TextField
                  {...field}
                  error={!!formState.errors['name']}
                  label={t('name', 'Name')}
                  fullWidth
                  required
                  helperText={
                    formState.errors['name']
                      ? t('fieldIsRequired', 'Field is required')
                      : t(
                          'pleaseMakeSureItsAUniqueField',
                          "Please make sure it's a unique {field}",
                          {
                            field: t('name', 'Name'),
                          },
                        )
                  }
                />
              )}
            />

            <Controller
              control={control}
              name='description'
              render={({ field }) => (
                <TextField {...field} label={t('description', 'Description')} fullWidth />
              )}
            />

            <Controller
              control={control}
              name='packageIds'
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  options={packageOptions}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  multiple
                  renderInput={params => (
                    <TextField {...params} fullWidth label={t('packages', 'Packages')} />
                  )}
                  filterOptions={(options, { inputValue }) =>
                    matchSorter(options, inputValue.replace(/\s/g, ''), {
                      keys: [item => item.label.replace(/\./g, '').replace(/\s/g, '')],
                      threshold: matchSorter.rankings.CONTAINS,
                    })
                  }
                />
              )}
            />

            <CommodityAttributeForm control={control} formState={formState} />
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Grid container spacing={theme.customSpacing.m}>
            <Grid
              size={{
                xs: 12,
              }}
            >
              <Typography variant='subtitle1'>{t('dimensions', 'Dimensions')}</Typography>
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <Controller
                control={control}
                name='length'
                render={({ field }) => (
                  <TextField
                    {...field}
                    type='number'
                    label={t('length', 'Length')}
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end'>mm</CustomInputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <Controller
                control={control}
                name='width'
                render={({ field }) => (
                  <TextField
                    {...field}
                    type='number'
                    label={t('width', 'Width')}
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end'>mm</CustomInputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <Controller
                control={control}
                name='height'
                render={({ field }) => (
                  <TextField
                    {...field}
                    type='number'
                    label={t('height', 'Height')}
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end'>mm</CustomInputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <Controller
                control={control}
                name='weight'
                render={({ field }) => (
                  <TextField
                    {...field}
                    type='number'
                    label={t('weight', 'Weight')}
                    fullWidth
                    slotProps={{
                      input: {
                        endAdornment: (
                          <CustomInputAdornment position='end'>kg</CustomInputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Grid>

            <Grid
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <Controller
                control={control}
                name='classDefinition'
                render={({ field }) => (
                  <TextField {...field} label={t('class', 'Class')} fullWidth />
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack gap={theme.customSpacing.m}>
            <Typography variant='subtitle1'>{t('other', 'Other')}</Typography>

            <Controller
              control={control}
              name={'isActive'}
              render={({ field }) => (
                <FormControlLabel
                  label={t('active', 'Active')}
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={event => field.onChange(event.target.checked)}
                      color='primary'
                    />
                  }
                />
              )}
            />

            <Controller
              control={control}
              name={'isSelectable'}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      {...field}
                      checked={field.value}
                      onChange={event => field.onChange(event.target.checked)}
                      color='primary'
                    />
                  }
                  label={t('selectable', 'Selectable')}
                />
              )}
            />

            <Controller
              control={control}
              name='instructions'
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('instructions', 'Instructions')}
                  fullWidth
                  multiline
                  minRows={2}
                  maxRows={3}
                />
              )}
            />
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}
